'use client';

import type { PingQuery, PingQueryVariables} from 'generated/graphql';
import { usePingQuery } from 'generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { ClientError } from 'graphql-request';
import { graphqlClient } from 'lib/api';
import { useEffect, useState } from 'react';
import { deleteCookie, getCookie } from 'cookies-next';
import { assignToken } from 'features/auth/utils';

export const PING_QUERY_KEY = ['Ping'];

export const useUpdatePing = () => {
  const queryClient = useQueryClient();

  return (data: PingQuery['ping']) => {
    if (data?.session?.token) {
      assignToken(data.session.token);
    }
    queryClient.setQueryData<PingQuery>(PING_QUERY_KEY, (oldData) => ({
      ...oldData,
      ping: { ...oldData?.ping, ...data },
    }));
  };
};

export const useInitializePing = () => {
  const [initializedGraphqlClient] = useState(() => {
    const cookieToken = getCookie(process.env.NEXT_PUBLIC_USER_AUTH_TOKEN_NAME!);
    if (cookieToken) {
      graphqlClient.setHeader('authorization', `Bearer ${cookieToken}`);
    }
    return graphqlClient;
  });

  const query = usePingQuery(initializedGraphqlClient, undefined, {
    queryKey: PING_QUERY_KEY,
    retry: false,
  });

  useEffect(() => {
    if (query.isError) {
      graphqlClient.setHeader('authorization', '');
      deleteCookie(process.env.NEXT_PUBLIC_USER_AUTH_TOKEN_NAME!);
    }
  }, [query.isError]);

  return null;
};

export default function usePing(
  variables?: PingQueryVariables,
  options?: Omit<UseQueryOptions<PingQuery, ClientError>, 'queryKey'> & {
    queryKey?: UseQueryOptions<PingQuery, ClientError>['queryKey'];
  },
  headers?: RequestInit['headers']
) {
  const query = usePingQuery(
    graphqlClient,
    variables,
    { queryKey: PING_QUERY_KEY, retry: false, enabled: false, ...options },
    headers
  );

  return {
    ...query,
    authUser: query.data?.ping?.user,
    authenticated: !!query.data?.ping?.user,
  };
}
